<template>
  <div>
    <label :for="inputName">{{inputLabel}}</label>
    <div class="phone-input-wrapper">
      <div class="phone-country-selector">
        <div class="country-flag">
          <img 
            :src="`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountryCode}.svg`"
            :alt="selectedCountryName"
            class="flag-icon"
          />
        </div>
        <select 
          :id="inputName + '-country'"
          class="country-select"
          v-model="selectedCountry"
          @change="onCountryChange"
        >
          <option 
            v-for="country in countries" 
            :key="country.code" 
            :value="country.code"
          >
            {{ country.name }} {{ country.dialCode }}
          </option>
        </select>
      </div>
      <input
        :disabled="disabled"
        :class="inputComputedClassName"
        type="tel"
        :name="inputName"
        :id="inputName"
        :value="phoneNumber"
        @input="validateAndSetInput"
        class="phone-number-input"
        :placeholder="placeholder"
      >
    </div>
    <span v-if="!isValid">{{errorText}}</span>
  </div>
</template>

<script>
const countries = [
  { code: 'RO', name: 'Romania', dialCode: '+40' },
  { code: 'US', name: 'United States', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
  { code: 'DE', name: 'Germany', dialCode: '+49' },
  { code: 'FR', name: 'France', dialCode: '+33' },
  { code: 'IT', name: 'Italy', dialCode: '+39' },
  { code: 'ES', name: 'Spain', dialCode: '+34' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31' },
  { code: 'BE', name: 'Belgium', dialCode: '+32' },
  { code: 'AT', name: 'Austria', dialCode: '+43' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41' },
  { code: 'PL', name: 'Poland', dialCode: '+48' },
  { code: 'HU', name: 'Hungary', dialCode: '+36' },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359' },
  { code: 'GR', name: 'Greece', dialCode: '+30' },
  { code: 'PT', name: 'Portugal', dialCode: '+351' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420' },
  { code: 'SE', name: 'Sweden', dialCode: '+46' },
  { code: 'NO', name: 'Norway', dialCode: '+47' },
  { code: 'DK', name: 'Denmark', dialCode: '+45' },
  { code: 'FI', name: 'Finland', dialCode: '+358' },
  { code: 'IE', name: 'Ireland', dialCode: '+353' },
  { code: 'SK', name: 'Slovakia', dialCode: '+421' },
  { code: 'SI', name: 'Slovenia', dialCode: '+386' },
  { code: 'HR', name: 'Croatia', dialCode: '+385' },
  { code: 'RS', name: 'Serbia', dialCode: '+381' },
  { code: 'MD', name: 'Moldova', dialCode: '+373' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380' },
  { code: 'TR', name: 'Turkey', dialCode: '+90' },
  { code: 'RU', name: 'Russia', dialCode: '+7' },
  { code: 'CA', name: 'Canada', dialCode: '+1' },
  { code: 'AU', name: 'Australia', dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64' },
  { code: 'BR', name: 'Brazil', dialCode: '+55' },
  { code: 'MX', name: 'Mexico', dialCode: '+52' },
  { code: 'AR', name: 'Argentina', dialCode: '+54' },
  { code: 'CL', name: 'Chile', dialCode: '+56' },
  { code: 'CO', name: 'Colombia', dialCode: '+57' },
  { code: 'PE', name: 'Peru', dialCode: '+51' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27' },
  { code: 'EG', name: 'Egypt', dialCode: '+20' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
  { code: 'IL', name: 'Israel', dialCode: '+972' },
  { code: 'JP', name: 'Japan', dialCode: '+81' },
  { code: 'CN', name: 'China', dialCode: '+86' },
  { code: 'IN', name: 'India', dialCode: '+91' },
  { code: 'KR', name: 'South Korea', dialCode: '+82' },
  { code: 'SG', name: 'Singapore', dialCode: '+65' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60' },
  { code: 'TH', name: 'Thailand', dialCode: '+66' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62' },
  { code: 'PH', name: 'Philippines', dialCode: '+63' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84' }
];

export default {
  props: {
    inputName: String,
    inputLabel: String,
    inputValue: String,
    setInput: Function,
    isValid: Boolean,
    disabled: Boolean,
    errorText: String,
    placeholder: {
      type: String,
      default: 'Număr de telefon'
    }
  },
  data() {
    // Extract phone number using the countries constant directly
    let phoneNumber = '';
    if (this.inputValue) {
      for (const country of countries) {
        if (this.inputValue.startsWith(country.dialCode)) {
          phoneNumber = this.inputValue.substring(country.dialCode.length).trim();
          break;
        }
      }
      if (!phoneNumber) {
        phoneNumber = this.inputValue;
      }
    }
    
    return {
      countries: countries,
      selectedCountry: 'RO',
      phoneNumber: phoneNumber
    };
  },
  computed: {
    inputComputedClassName: function() {
      return this.isValid ? "" : " error";
    },
    selectedCountryCode: function() {
      return this.selectedCountry;
    },
    selectedCountryName: function() {
      const country = this.countries.find(c => c.code === this.selectedCountry);
      return country ? country.name : 'Romania';
    },
    selectedDialCode: function() {
      const country = this.countries.find(c => c.code === this.selectedCountry);
      return country ? country.dialCode : '+40';
    }
  },
  watch: {
    inputValue: function(newVal) {
      if (newVal) {
        const extracted = this.extractPhoneNumber(newVal);
        if (extracted !== this.phoneNumber) {
          this.phoneNumber = extracted;
        }
      }
    }
  },
  methods: {
    extractPhoneNumber(value) {
      if (!value) return '';
      // Remove country code if present
      // Use countries constant directly to avoid issues with this.countries not being available
      for (const country of countries) {
        if (value.startsWith(country.dialCode)) {
          return value.substring(country.dialCode.length).trim();
        }
      }
      return value;
    },
    onCountryChange() {
      this.updatePhoneValue();
    },
    validateAndSetInput(event) {
      this.phoneNumber = event.target.value;
      this.updatePhoneValue();
    },
    updatePhoneValue() {
      const fullNumber = this.selectedDialCode + ' ' + this.phoneNumber;
      const fakeEvent = {
        target: {
          name: this.inputName,
          value: fullNumber.trim()
        }
      };
      this.setInput(fakeEvent);
    }
  },
  mounted() {
    // Extract country from existing value if present
    if (this.inputValue) {
      for (const country of this.countries) {
        if (this.inputValue.startsWith(country.dialCode)) {
          this.selectedCountry = country.code;
          this.phoneNumber = this.inputValue.substring(country.dialCode.length).trim();
          break;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.4;
}

.phone-input-wrapper {
  display: flex;
  align-items: stretch;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background-color: #f8fafc;
  transition: all 0.2s ease;
  overflow: hidden;
  
  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
  }
  
  &:focus-within {
    border-color: #bd1f23;
    background-color: #fff;
    box-shadow: 0 0 0 1px rgba(189, 31, 35, 0.1);
  }
  
  &.error {
    border-color: #bd1f23;
    background-color: #fff;
  }
}

.phone-country-selector {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 8px 0 12px;
  background-color: transparent;
  border-right: 1px solid #e2e8f0;
  gap: 8px;
  flex-shrink: 0;
  max-width: 180px;
  min-width: 140px;
  
  .country-flag {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    
    .flag-icon {
      width: 20px;
      height: 15px;
      object-fit: cover;
      border-radius: 2px;
      display: block;
    }
  }
  
  .country-select {
    appearance: none;
    border: none;
    background: transparent;
    padding: 11px 4px 11px 0;
    font-size: 14px;
    font-weight: 500;
    color: #1a1a1a;
    cursor: pointer;
    outline: none;
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    &:focus {
      outline: none;
    }
  }
}

.phone-number-input {
  flex: 1;
  border: none;
  padding: 11px 16px;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  background-color: transparent;
  color: #1a1a1a;
  outline: none;
  text-align: center;
  line-height: 22px;
  
  &::placeholder {
    color: #94a3b8;
    opacity: 1;
    text-align: center;
  }
  
  &:disabled {
    background-color: #f1f5f9;
    cursor: not-allowed;
    color: #94a3b8;
  }
  
  &.error {
    color: #bd1f23;
  }
}

span {
  display: block;
  margin: 6px 0 0 0;
  color: #bd1f23;
  font-size: 14px;
}
</style>

